import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Loader2, BookOpen, ChevronDown, ChevronRight, Pencil, Trash2, Check, X, MapPin, Navigation, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StoryBibleTab = ({ campaignId, messageCount = 0 }) => {
  const [bibles, setBibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [description, setDescription] = useState('');
  const [expandedBook, setExpandedBook] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [editData, setEditData] = useState({});
  const [pollInterval, setPollInterval] = useState(null);
  const [prevPosition, setPrevPosition] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [activeInfo, setActiveInfo] = useState({ active_bible_id: null, primary_bible_id: null, is_pinned: false });

  const fetchActiveBible = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/active-bible`, { withCredentials: true });
      setActiveInfo(res.data || { active_bible_id: null, primary_bible_id: null, is_pinned: false });
    } catch (err) {
      // Non-critical — UI just won't show badges
    }
  }, [campaignId]);

  const fetchBibles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/story-bibles/${campaignId}`, { withCredentials: true });
      setBibles(res.data.bibles || []);
      
      const genBible = (res.data.bibles || []).find(b => b.status === 'generating');
      if (genBible) {
        setGenerating(true);
      } else {
        setGenerating(false);
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
      }
      // Refresh allocator decision whenever bibles list changes
      fetchActiveBible();
    } catch (err) {
      if (err.response?.status !== 401) console.error('Failed to fetch bibles:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, pollInterval, fetchActiveBible]);

  useEffect(() => {
    fetchBibles();
  }, [fetchBibles]);

  // Re-fetch story bible position after each chat message
  useEffect(() => {
    if (messageCount > 0) {
      const timer = setTimeout(fetchBibles, 2000); // slight delay for backend to process
      return () => clearTimeout(timer);
    }
  }, [messageCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while generating
  useEffect(() => {
    if (generating && !pollInterval) {
      const interval = setInterval(fetchBibles, 3000);
      setPollInterval(interval);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [generating, pollInterval, fetchBibles]);

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Describe the books or series you want the GM to follow.');
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/story-bibles/generate`, {
        campaign_id: campaignId,
        description: description.trim(),
      }, { withCredentials: true });
      toast.success('Generating story bible...');
      setDescription('');
      // Start polling
      const interval = setInterval(fetchBibles, 3000);
      setPollInterval(interval);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const handleDelete = async (bibleId) => {
    // Inline confirmation flow — first click stages, second click commits.
    // Avoids native window.confirm (unreliable in iframe/preview contexts).
    if (confirmDeleteId !== bibleId) {
      setConfirmDeleteId(bibleId);
      // Auto-cancel after 5 seconds so the UI doesn't get stuck
      setTimeout(() => {
        setConfirmDeleteId((current) => (current === bibleId ? null : current));
      }, 5000);
      return;
    }

    try {
      // If the bible being deleted is pinned, unpin it first so we don't
      // leave a dangling primary_bible_id reference on the campaign.
      if (activeInfo.primary_bible_id === bibleId) {
        try {
          await axios.post(
            `${API}/campaigns/${campaignId}/pin-bible`,
            { bible_id: null },
            { withCredentials: true }
          );
        } catch (_) { /* unpin failure is non-fatal */ }
      }

      await axios.delete(`${API}/story-bibles/${bibleId}`, { withCredentials: true });
      setBibles(prev => prev.filter(b => b.bible_id !== bibleId));
      setConfirmDeleteId(null);
      toast.success('Story bible deleted');
      fetchActiveBible();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      console.error('[StoryBibleTab] Delete failed:', err?.response?.status, detail);
      toast.error(`Failed to delete: ${detail}`);
      setConfirmDeleteId(null);
    }
  };

  const togglePin = async (bibleId) => {
    const isPinned = activeInfo.is_pinned && activeInfo.primary_bible_id === bibleId;
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/pin-bible`,
        { bible_id: isPinned ? null : bibleId },
        { withCredentials: true }
      );
      setActiveInfo(prev => ({ ...prev, primary_bible_id: res.data.primary_bible_id, is_pinned: !!res.data.primary_bible_id }));
      // Refresh full state in case era routing now picks a different bible
      fetchActiveBible();
      toast.success(isPinned ? 'Unpinned — auto-routing restored' : 'Pinned as primary canon');
    } catch (err) {
      toast.error('Pin update failed');
    }
  };

  const startEditChapter = (bibleIdx, bookIdx, chapterIdx, chapter) => {
    setEditingChapter({ bibleIdx, bookIdx, chapterIdx });
    setEditData({
      in_game_time: chapter.in_game_time || '',
      location: chapter.location || '',
      events: chapter.events || '',
      key_details: chapter.key_details || '',
      characters_present: (chapter.characters_present || []).join(', '),
      plot_critical_moments: (chapter.plot_critical_moments || []).join(', '),
    });
  };

  const saveChapterEdit = async () => {
    const { bibleIdx, bookIdx, chapterIdx } = editingChapter;
    const bible = bibles[bibleIdx];
    try {
      await axios.put(
        `${API}/story-bibles/${bible.bible_id}/books/${bookIdx}/chapters/${chapterIdx}`,
        {
          in_game_time: editData.in_game_time,
          location: editData.location,
          events: editData.events,
          key_details: editData.key_details,
          characters_present: editData.characters_present.split(',').map(s => s.trim()).filter(Boolean),
          plot_critical_moments: editData.plot_critical_moments.split(',').map(s => s.trim()).filter(Boolean),
        },
        { withCredentials: true }
      );
      toast.success('Chapter updated');
      setEditingChapter(null);
      fetchBibles();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const updatePosition = async (bibleId, bookIndex, chapterIndex) => {
    try {
      await axios.put(
        `${API}/story-bibles/${bibleId}/position`,
        { current_book_index: bookIndex, current_chapter_index: chapterIndex },
        { withCredentials: true }
      );
      toast.success(`Story position set to Book ${bookIndex + 1}, Chapter ${chapterIndex + 1}`);
      fetchBibles();
    } catch (err) {
      toast.error('Failed to update position');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  }

  const readyBibles = bibles.filter(b => b.status === 'ready');
  const generatingBible = bibles.find(b => b.status === 'generating');

  return (
    <div className="space-y-4" data-testid="story-bible-tab">
      <h3 className="text-lg font-bold text-emerald-400 font-mono">Story Bible</h3>
      <p className="text-slate-500 text-xs">
        Add books or series for the GM to follow as canonical story. The AI generates chapter-by-chapter references you can review and edit.
      </p>

      {/* Generation Input */}
      {!generatingBible && (
        <div className="space-y-2">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={"Republic Commando series by Karen Traviss — Hard Contact, Triple Zero, True Colors, Order 66.\n\nOr: Horus Heresy first trilogy. Or: Original Star Wars trilogy timeline."}
            className="w-full h-24 bg-slate-950/50 border border-white/10 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-600 resize-none focus:border-emerald-400/40 focus:outline-none"
            data-testid="story-bible-input"
          />
          <Button
            onClick={handleGenerate}
            disabled={generating || !description.trim()}
            className="w-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-400/30"
            data-testid="generate-bible-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BookOpen className="h-4 w-4 mr-2" />}
            {generating ? 'Generating...' : 'Generate Story Bible'}
          </Button>
        </div>
      )}

      {/* Generation Progress */}
      {generatingBible && (
        <div className="glass-card p-4 rounded-lg border border-emerald-500/20 space-y-2" data-testid="bible-generating">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Generating Story Bible...</span>
          </div>
          <p className="text-slate-400 text-xs">
            {generatingBible.generation_progress?.current_book || 'Planning...'}
            {generatingBible.generation_progress?.total_books > 0 && (
              <span className="ml-2 text-slate-500">
                ({generatingBible.generation_progress.completed_books}/{generatingBible.generation_progress.total_books} books)
              </span>
            )}
          </p>
          <div className="w-full bg-slate-800 rounded-full h-1.5">
            <div
              className="bg-emerald-400 h-1.5 rounded-full transition-all"
              style={{
                width: generatingBible.generation_progress?.total_books
                  ? `${(generatingBible.generation_progress.completed_books / generatingBible.generation_progress.total_books) * 100}%`
                  : '10%'
              }}
            />
          </div>
        </div>
      )}

      {/* Ready Bibles */}
      {readyBibles.map((bible, bibleIdx) => {
        const pos = bible.story_position || {};
        const currentBookIdx = pos.current_book_index || 0;
        const currentChIdx = pos.current_chapter_index || 0;
        const isActive = activeInfo.active_bible_id === bible.bible_id;
        const isPinned = activeInfo.is_pinned && activeInfo.primary_bible_id === bible.bible_id;
        const showMultiBibleUI = readyBibles.length > 1;

        return (
          <div
            key={bible.bible_id}
            className={`glass-card rounded-lg overflow-hidden border ${isPinned ? 'border-blue-400/50' : isActive ? 'border-emerald-400/40' : 'border-white/5'}`}
            data-testid={`bible-${bible.bible_id}`}
          >
            {/* Bible Header */}
            <div className={`flex items-center justify-between p-3 ${isPinned ? 'bg-blue-500/10' : isActive ? 'bg-emerald-500/10' : 'bg-slate-900/30'}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className={`text-sm font-bold truncate ${isPinned ? 'text-blue-300' : isActive ? 'text-emerald-300' : 'text-slate-300'}`}>{bible.title}</h4>
                  {isPinned && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-400/40 text-blue-300 tracking-wider" data-testid={`bible-badge-pinned-${bible.bible_id}`}>
                      PINNED
                    </span>
                  )}
                  {isActive && !isPinned && showMultiBibleUI && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 tracking-wider" data-testid={`bible-badge-active-${bible.bible_id}`}>
                      ACTIVE
                    </span>
                  )}
                  {!isActive && showMultiBibleUI && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-700/30 border border-slate-600/30 text-slate-500" data-testid={`bible-badge-frozen-${bible.bible_id}`}>
                      FROZEN
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{bible.description?.slice(0, 80)}{bible.description?.length > 80 ? '...' : ''}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {showMultiBibleUI && (
                  <Button
                    onClick={() => togglePin(bible.bible_id)}
                    size="sm"
                    variant="ghost"
                    title={isPinned ? 'Unpin (restore auto-routing)' : 'Pin as primary canon'}
                    className={`h-7 w-7 p-0 ${isPinned ? 'text-blue-400 hover:text-blue-300' : 'text-slate-500 hover:text-blue-400'}`}
                    data-testid={`bible-pin-btn-${bible.bible_id}`}
                  >
                    {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </Button>
                )}
                <Button
                  onClick={() => handleDelete(bible.bible_id)}
                  size="sm"
                  variant="ghost"
                  title={confirmDeleteId === bible.bible_id ? 'Click again to confirm delete' : 'Delete this story bible'}
                  className={`h-7 ${confirmDeleteId === bible.bible_id ? 'w-auto px-2 bg-red-500/30 text-red-200 border border-red-400/50' : 'w-7 p-0 text-red-400/50 hover:text-red-400'}`}
                  data-testid={`bible-delete-btn-${bible.bible_id}`}
                >
                  {confirmDeleteId === bible.bible_id ? (
                    <span className="text-[10px] font-mono font-bold tracking-wider">CONFIRM?</span>
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>

            {/* Current Position — prominent display */}
            <div className="px-3 py-2 border-t border-white/5 bg-emerald-500/5">
              <div className="flex items-center gap-2">
                <Navigation className="h-3.5 w-3.5 text-emerald-400 flex-shrink-0" />
                <p className="text-xs text-slate-300">
                  AI is reading: <span className="text-emerald-400 font-bold">
                    {bible.books[currentBookIdx]?.title || '?'}, Chapter {currentChIdx + 1}
                  </span>
                </p>
              </div>
              {pos.divergences?.length > 0 && (
                <p className="text-xs text-amber-400/70 mt-1 ml-5">{pos.divergences.length} divergence{pos.divergences.length > 1 ? 's' : ''} from canon</p>
              )}
            </div>

            {/* Books Accordion */}
            {bible.books.map((book, bookIdx) => (
              <div key={bookIdx} className="border-t border-white/5">
                <button
                  onClick={() => setExpandedBook(expandedBook === `${bibleIdx}-${bookIdx}` ? null : `${bibleIdx}-${bookIdx}`)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors"
                >
                  <span className="text-xs font-medium text-slate-300">
                    {bookIdx === currentBookIdx && <span className="text-emerald-400 mr-1">&#9654;</span>}
                    {book.title}
                    <span className="text-slate-500 ml-2">({book.chapters?.length || 0} chapters)</span>
                  </span>
                  {expandedBook === `${bibleIdx}-${bookIdx}` ? <ChevronDown className="h-3 w-3 text-slate-500" /> : <ChevronRight className="h-3 w-3 text-slate-500" />}
                </button>

                {expandedBook === `${bibleIdx}-${bookIdx}` && (
                  <div className="px-3 pb-2 space-y-1">
                    {book.chapters?.map((chapter, chIdx) => {
                      const isCurrent = bookIdx === currentBookIdx && chIdx === currentChIdx;
                      const isEditing = editingChapter?.bibleIdx === bibleIdx && editingChapter?.bookIdx === bookIdx && editingChapter?.chapterIdx === chIdx;

                      return (
                        <div
                          key={chIdx}
                          className={`rounded p-2 text-xs transition-all duration-500 ${
                            isCurrent
                              ? 'bg-emerald-500/15 border-2 border-emerald-500/40 shadow-lg shadow-emerald-500/5'
                              : 'bg-slate-900/30 border border-white/5 hover:border-white/10'
                          }`}
                          data-testid={`bible-chapter-${bookIdx}-${chIdx}`}
                        >
                          {isEditing ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-2 gap-2">
                                <input value={editData.in_game_time} onChange={(e) => setEditData({...editData, in_game_time: e.target.value})} placeholder="Time" className="bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200" />
                                <input value={editData.location} onChange={(e) => setEditData({...editData, location: e.target.value})} placeholder="Location" className="bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200" />
                              </div>
                              <textarea value={editData.events} onChange={(e) => setEditData({...editData, events: e.target.value})} placeholder="Events..." rows={3} className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200 resize-none" />
                              <input value={editData.characters_present} onChange={(e) => setEditData({...editData, characters_present: e.target.value})} placeholder="Characters (comma-separated)" className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200" />
                              <input value={editData.plot_critical_moments} onChange={(e) => setEditData({...editData, plot_critical_moments: e.target.value})} placeholder="Critical moments (comma-separated)" className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200" />
                              <textarea value={editData.key_details} onChange={(e) => setEditData({...editData, key_details: e.target.value})} placeholder="Key details..." rows={2} className="w-full bg-slate-900 border border-white/10 rounded px-2 py-1 text-xs text-slate-200 resize-none" />
                              <div className="flex gap-1">
                                <Button onClick={saveChapterEdit} size="sm" className="h-6 text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"><Check className="h-3 w-3 mr-1" /> Save</Button>
                                <Button onClick={() => setEditingChapter(null)} size="sm" variant="ghost" className="h-6 text-xs text-slate-400"><X className="h-3 w-3 mr-1" /> Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center justify-between mb-1">
                                <span className={`font-medium ${isCurrent ? 'text-emerald-400' : 'text-slate-300'}`}>
                                  {isCurrent && <Navigation className="h-3 w-3 inline mr-1" />}
                                  Ch.{chapter.chapter_num || chIdx + 1}
                                  {isCurrent && <span className="ml-1 text-emerald-300 text-[10px] font-normal">(AI reading)</span>}
                                </span>
                                <div className="flex gap-1.5 items-center">
                                  {!isCurrent && (
                                    <button
                                      onClick={() => updatePosition(bible.bible_id, bookIdx, chIdx)}
                                      className="flex items-center gap-1 text-[10px] text-emerald-400/50 hover:text-emerald-400 bg-emerald-400/5 hover:bg-emerald-400/15 px-1.5 py-0.5 rounded transition-colors"
                                      title="Set the AI to read from this chapter"
                                      data-testid={`set-position-${bookIdx}-${chIdx}`}
                                    >
                                      <MapPin className="h-2.5 w-2.5" />
                                      Read from here
                                    </button>
                                  )}
                                  <button onClick={() => startEditChapter(bibleIdx, bookIdx, chIdx, chapter)} className="text-blue-400/50 hover:text-blue-400" title="Edit">
                                    <Pencil className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                              <p className="text-slate-500">{chapter.in_game_time} — {chapter.location}</p>
                              <p className="text-slate-300 mt-1">{chapter.events}</p>
                              {chapter.characters_present?.length > 0 && (
                                <p className="text-slate-500 mt-1">Characters: {chapter.characters_present.join(', ')}</p>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}

      {readyBibles.length === 0 && !generatingBible && (
        <div className="text-center py-4">
          <p className="text-slate-500 text-sm">No story bibles yet.</p>
          <p className="text-slate-600 text-xs mt-1">Add source material above for the GM to follow canonical storylines.</p>
        </div>
      )}
    </div>
  );
};
